import React, { createContext, useContext, ReactNode } from 'react';
import { Control, FieldErrors, UseFieldArrayReturn } from 'react-hook-form';
import { TransformedStep, GuideBuilderFormData } from '../components/guide-builder/schemas';
import { TutorialStep } from '../services/api';
import { GuideDetailsFormData } from '@/components/guide-builder/schemas';

// Split contexts by concern to minimize re-renders
interface GuideBuilderFormContextValue {
  control: Control<GuideBuilderFormData>;
  errors: FieldErrors<GuideBuilderFormData>;
  stepsFieldArray: UseFieldArrayReturn<GuideDetailsFormData>;
}

interface GuideBuilderStateContextValue {
  isPreviewMode: boolean;
  selectedStep: TransformedStep | undefined;
  isPublishing: boolean;
  isTranslating: boolean;
}

interface GuideBuilderActionsContextValue {
  // onPreviewToggle: () => void;
  onSelectStep: (step: TransformedStep | undefined) => void;
  onPreviewToggle: () => void;
  setIsPublishing: (isPublishing: boolean) => void;
  setIsTranslating: (isTranslating: boolean) => void;
}

interface GuideBuilderDataContextValue {
  tutorialState: {
    title: string;
    description: string;
    difficulty: string;
    estimatedTime: string;
    category: string;
    product: string;
    language: string;
    steps: TutorialStep[];
  };
}

// Create contexts
const GuideBuilderGuideContext = createContext<
  | {
      guide: GuideBuilderFormData;
      actions: GuideBuilderActionsContextValue;
      state: GuideBuilderStateContextValue;
      goBackPath?: string;
    }
  | undefined
>(undefined);
const GuideBuilderFormContext = createContext<GuideBuilderFormContextValue | null>(null);
const GuideBuilderStateContext = createContext<GuideBuilderStateContextValue | null>(null);
const GuideBuilderActionsContext = createContext<GuideBuilderActionsContextValue | null>(null);
const GuideBuilderDataContext = createContext<GuideBuilderDataContextValue | null>(null);

// Provider component
interface GuideBuilderProviderProps {
  children: ReactNode;
  guide: GuideBuilderFormData;
  actions: GuideBuilderActionsContextValue;
  state: GuideBuilderStateContextValue;
  goBackPath?: string;
}

export const GuideBuilderProvider: React.FC<GuideBuilderProviderProps> = ({
  guide,
  actions,
  state,
  goBackPath,
  children,
}) => {
  return (
    <GuideBuilderGuideContext.Provider value={{ guide, actions, state, goBackPath }}>
      {children}
    </GuideBuilderGuideContext.Provider>
  );
};

// Custom hooks for consuming contexts
export const useGuideBuilderGuide = () => {
  const context = useContext(GuideBuilderGuideContext);
  if (!context) {
    throw new Error('useGuideBuilderGuide must be used within GuideBuilderProvider');
  }
  return context;
};

export const useGuideBuilderForm = () => {
  const context = useContext(GuideBuilderFormContext);
  if (!context) {
    throw new Error('useGuideBuilderForm must be used within GuideBuilderProvider');
  }
  return context;
};

export const useGuideBuilderState = () => {
  const context = useContext(GuideBuilderStateContext);
  if (!context) {
    throw new Error('useGuideBuilderState must be used within GuideBuilderProvider');
  }
  return context;
};

export const useGuideBuilderActions = () => {
  const context = useContext(GuideBuilderActionsContext);
  if (!context) {
    throw new Error('useGuideBuilderActions must be used within GuideBuilderProvider');
  }
  return context;
};

export const useGuideBuilderData = () => {
  const context = useContext(GuideBuilderDataContext);
  if (!context) {
    throw new Error('useGuideBuilderData must be used within GuideBuilderProvider');
  }
  return context;
};

// Combined hook for convenience (use sparingly to avoid re-renders)
export const useGuideBuilder = () => {
  return {
    form: useGuideBuilderForm(),
    state: useGuideBuilderState(),
    actions: useGuideBuilderActions(),
    data: useGuideBuilderData(),
  };
};
